import * as React from "react";
import { useNavigate } from "@opendash/router";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  Card,
  InfoBox,
  ImageContainer,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

const b2Bild1 = require("../../assets/b2_schild.png");
const b2Bild2 = require("../../assets/b2_zonen.png");

export const TrinkwasserqualitaetRoute = React.memo(
  function TrinkwasserqualitaetRoute() {
    const navigate = useNavigate();
    const { t, getKeysWithPrefix } = useTexts();

    const literaturNumbers = React.useMemo(() => {
      const textKeys = getKeysWithPrefix("B.2.literatur.");
      const numbers = new Set<number>();
      textKeys.forEach((key) => {
        const match = key.match(/^B\.2\.literatur\.(\d+)\./);
        if (match) {
          numbers.add(parseInt(match[1], 10));
        }
      });
      return Array.from(numbers).sort((a, b) => a - b);
    }, [getKeysWithPrefix]);

    const renderParagraphs = (text: string) => {
      if (!text) return null;
      return text.split("\n\n").map((paragraph, index) => {
        if (paragraph.includes("\n•") || paragraph.startsWith("•")) {
          const items = paragraph
            .split("\n")
            .filter((item) => item.trim().startsWith("•"));
          const intro = paragraph.split("\n")[0];
          return (
            <div key={index}>
              {!intro.startsWith("•") && (
                <p>{processInlineFormatting(intro)}</p>
              )}
              <ul>
                {items.map((item, i) => (
                  <li key={i}>
                    {processInlineFormatting(item.replace(/^•\s*/, ""))}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return <p key={index}>{processInlineFormatting(paragraph)}</p>;
      });
    };

    const tabs = [
      {
        title: t("B.tab.1.title", "Wie funktioniert ein Wasserwerk?"),
        path: "/wasserklaerer/wasseraufbereitung/wasserwerk",
        isActive: false,
      },
      {
        title: t("B.tab.2.title", "Trinkwasserqualität"),
        path: "/wasserklaerer/wasseraufbereitung/qualitaet",
        isActive: true,
      },
      {
        title: t("B.tab.3.title", "Verteilnetz des Trinkwassers"),
        path: "/wasserklaerer/wasseraufbereitung/verteilnetz",
        isActive: false,
      },
    ];

    return (
      <AppLayout>
        <ContentContainer>
          <h1>{t("B.2.title", "Trinkwasserqualität")}</h1>

          <TabContainer>
            {tabs.map((tab, index) => (
              <TabButton
                key={index}
                isActive={tab.isActive}
                onClick={() => !tab.isActive && navigate(tab.path)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabContainer>

          {/* Abschnitt 1: Trinkwasserverordnung */}
          <Card>
            <h2>
              {t("B.2.trinkwv.title", "Was ist die Trinkwasserverordnung?")}
            </h2>
            {renderParagraphs(t("B.2.trinkwv.text", ""))}
          </Card>

          {/* Abschnitt 2: Qualität */}
          <Card>
            <h2>
              {t(
                "B.2.qualitaet.title",
                "Wie steht es um die Qualität des Wassers?"
              )}
            </h2>
            {renderParagraphs(t("B.2.qualitaet.text", ""))}

            <InfoBox>
              <h4>
                {t(
                  "B.2.qualitaet.ergebnis.title",
                  "Ergebnis des 7. Qualitätsberichts (2020-2022)"
                )}
              </h4>
              {renderParagraphs(t("B.2.qualitaet.ergebnis.text", ""))}
            </InfoBox>
          </Card>

          {/* Abschnitt 3: Wasserschutzgebiete */}
          <Card>
            <h2>{t("B.2.schutzgebiete.title", "Wasserschutzgebiete")}</h2>
            {renderParagraphs(t("B.2.schutzgebiete.text", ""))}

            <ImageContainer>
              <img src={b2Bild1} alt="Verkehrszeichen Wasserschutzgebiet" />
              <FigureCaption>
                Abbildung 1:{" "}
                {t("B.2.bild1.caption", "Verkehrszeichen Wasserschutzgebiet")}
              </FigureCaption>
            </ImageContainer>
          </Card>

          {/* Abschnitt 4: Die Zonen */}
          <Card>
            <h2>{t("B.2.zonen.title", "Die Schutzzonen")}</h2>

            <h3>{t("B.2.zone1.title", "Zone 1 (Fassungsbereich)")}</h3>
            {renderParagraphs(t("B.2.zone1.text", ""))}

            <h3>{t("B.2.zone2.title", "Zone 2 (engere Schutzzone)")}</h3>
            {renderParagraphs(t("B.2.zone2.text", ""))}

            <h3>{t("B.2.zone3.title", "Zone 3 (weitere Schutzzone)")}</h3>
            {renderParagraphs(t("B.2.zone3.text", ""))}

            <ImageContainer>
              <img src={b2Bild2} alt="Schutzzonen Wassergebiete" />
              <FigureCaption>
                Abbildung 2:{" "}
                {t(
                  "B.2.bild2.caption",
                  "Schutzzonen Wassergebiete (eigene Illustration)"
                )}
              </FigureCaption>
            </ImageContainer>
          </Card>

          {/* Quellenverzeichnis */}
          <SourcesSection>
            <h3>{t("B.2.literatur.title", "Literatur")}</h3>
            <ol>
              {literaturNumbers.map((num) => (
                <li key={num} id={`quelle-${num}`}>
                  <LinkifyText>
                    {[
                      t(`B.2.literatur.${num}.text`, ""),
                      t(`B.2.literatur.${num}.url`, ""),
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </LinkifyText>
                </li>
              ))}
            </ol>
          </SourcesSection>
        </ContentContainer>
      </AppLayout>
    );
  }
);
